import {
  flexRender,
  type CellContext,
  type ColumnDef,
  type Row,
  type RowData,
} from '@tanstack/react-table';
import { Loader2Icon, PencilIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type * as z from 'zod';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/core/tooltip';
import { cn, extractErrorMessage } from '@/shared/helpers';
import type { Result } from '@/shared/helpers/Result';
import { useStateWithRef } from '@/shared/hooks/use-state-with-ref';

import { useRowEditingContext } from '../row/row-editing-context';
import type { EditableColumnEditor, EditableWritableColumnMeta } from '../types';
import { useCellEditingContext } from './cell-editing-context';
import { EditableSelectInput } from './editable-select-input';
import { EditableTextInput } from './editable-text-input';
import { useEditableCellValue } from './use-editable-cell-value';

export type EditableCellCommitArgs<TData extends RowData> = {
  row: TData;
  rowId: string;
  columnId: string;
  value: unknown;
};

export type EditableCellCommitResult = void | Result<unknown> | Promise<void | Result<unknown>>;

export type EditableCellEditRequestArgs<TData extends RowData> = {
  row: Row<TData>;
  rowId: string;
  columnId: string;
  cellKey: string;
};

type EditorMode = 'idle' | 'editing' | 'committing';
type CellDraftValue = string;

type EditableCellDisplayProps<TData extends RowData, TValue = unknown> = {
  context: CellContext<TData, TValue>;
  column: ColumnDef<TData, TValue>;
  columnId: string;
  label: string;
  meta: EditableWritableColumnMeta<TValue>;
  fieldSchema: z.ZodType;
  onCommit: (args: EditableCellCommitArgs<TData>) => EditableCellCommitResult;
  onRequestEdit: (args: EditableCellEditRequestArgs<TData>) => void;
};

function isPromiseLike(value: unknown): value is Promise<void | Result<unknown>> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'then' in value &&
    typeof value.then === 'function'
  );
}

function toSerializedDraftValue(value: unknown) {
  return value === null || value === undefined ? '' : String(value);
}

function normalizeEditorValue(editor: EditableColumnEditor, rawValue: CellDraftValue) {
  if (editor.type === 'number') {
    return rawValue === '' ? null : Number(rawValue);
  }
  return rawValue;
}

function getInitialComparableValue<TValue>(
  meta: EditableWritableColumnMeta<TValue>,
  fieldSchema: z.ZodType,
  value: unknown,
) {
  const normalizedValue = normalizeEditorValue(meta.editor, toSerializedDraftValue(value));
  const parsedValue = fieldSchema.safeParse(normalizedValue);
  return parsedValue.success ? parsedValue.data : normalizedValue;
}

export function EditableCellDisplay<TData extends RowData, TValue = unknown>({
  context,
  column,
  columnId,
  label,
  meta,
  fieldSchema,
  onCommit,
  onRequestEdit,
}: EditableCellDisplayProps<TData, TValue>) {
  const { getActiveCellKey, clearActiveCell, restoreFailedCell, registerCellController } =
    useCellEditingContext();
  const rowEditing = useRowEditingContext();
  const [mode, setMode, modeRef] = useStateWithRef<EditorMode>('idle');
  const [draftValue, setDraftValue, draftValueRef] = useStateWithRef<CellDraftValue>('');
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const valueBeforeEditRef = useRef<unknown>(undefined);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const cellElementRef = useRef<HTMLTableCellElement | null>(null);

  const rowId = context.row.id;
  const cellKey = `${rowId}:${columnId}`;
  const { value, displayContext, stageValue, clearValue } = useEditableCellValue({
    context,
    rowId,
    columnId,
  });
  const isRowEditLocked = rowEditing ? rowEditing.isSaving || rowEditing.isDeleting : false;

  function updateDraftValue(
    nextDraftValue: CellDraftValue,
    nextValidationMessage: string | null = null,
  ) {
    setDraftValue(nextDraftValue);
    setValidationMessage(nextValidationMessage);
  }

  // When we open the editor, we need to lock the cell width to ensure the table's auto layout
  // doesn't make the column pop due to the <button> changing into an input component.
  function lockCellSize() {
    // We have to apply the width lock to the cell, as opposed to the input, because when the table
    // is larger than necessary the browser's auto layout will choose to expand the column even if
    // the input element is constrained.
    const cellElement = buttonRef.current?.closest('td');
    if (!(cellElement instanceof HTMLTableCellElement)) {
      return;
    }
    cellElementRef.current = cellElement;

    const measuredWidth = cellElement.getBoundingClientRect().width;
    if (!measuredWidth || measuredWidth <= 0) {
      return;
    }

    const widthStyle = `${measuredWidth}px`;
    cellElement.style.width = widthStyle;
    cellElement.style.minWidth = widthStyle;
    cellElement.style.maxWidth = widthStyle;
  }

  function releaseCellSize() {
    const cellElement = cellElementRef.current;
    if (cellElement) {
      cellElement.style.width = '';
      cellElement.style.minWidth = '';
      cellElement.style.maxWidth = '';
    }
    cellElementRef.current = null;
  }

  function resetEditor({ preserveSizeLock = false }: { preserveSizeLock?: boolean } = {}) {
    if (!preserveSizeLock) {
      releaseCellSize();
    }
    setMode('idle');
    updateDraftValue('');
    valueBeforeEditRef.current = undefined;
  }

  function activateEditor() {
    if (modeRef.current !== 'idle' || isRowEditLocked) {
      return false;
    }

    lockCellSize();
    setMode('editing');
    updateDraftValue(toSerializedDraftValue(value));
    // Keep the pre-edit comparable value so a live row update does not turn a no-op edit into a new commit.
    valueBeforeEditRef.current = getInitialComparableValue(meta, fieldSchema, value);
    return true;
  }

  function completeCellCommit({
    currentDraftValue,
    parsedValue,
  }: {
    currentDraftValue: string;
    parsedValue: unknown;
  }) {
    setMode('committing');
    setValidationMessage(null);
    stageValue(parsedValue);

    function finishCommitFailure(message: string) {
      clearValue();

      if (restoreFailedCell(cellKey)) {
        setMode('editing');
        updateDraftValue(currentDraftValue, message);
        return;
      }

      resetEditor();
    }

    function finishCommit(result?: void | Result<unknown>) {
      if (result?.isFailure) {
        finishCommitFailure(result.error ?? 'Unable to save value');
        return;
      }

      resetEditor();
    }

    let commitResult: EditableCellCommitResult;
    try {
      commitResult = onCommit({
        row: context.row.original,
        rowId,
        columnId,
        value: parsedValue,
      });
    } catch (e) {
      finishCommitFailure(extractErrorMessage(e) ?? 'Unable to save value');
      return false;
    }

    if (isPromiseLike(commitResult)) {
      void commitResult.then(finishCommit).catch((e) => {
        finishCommitFailure(extractErrorMessage(e) ?? 'Unable to save value');
      });
      return true;
    }

    if (commitResult?.isFailure) {
      finishCommitFailure(commitResult.error ?? 'Unable to save value');
      return false;
    }

    finishCommit(commitResult);
    return true;
  }

  function submitEditor(
    nextRawDraftValue?: CellDraftValue,
    { preserveSizeLock = false }: { preserveSizeLock?: boolean } = {},
  ) {
    if (modeRef.current !== 'editing') {
      return true;
    }

    if (nextRawDraftValue !== undefined) {
      updateDraftValue(nextRawDraftValue);
    }

    const currentDraftValue = nextRawDraftValue ?? draftValueRef.current;
    const normalizedValue = normalizeEditorValue(meta.editor, currentDraftValue);
    const parsedValue = fieldSchema.safeParse(normalizedValue);

    if (!parsedValue.success) {
      setValidationMessage(parsedValue.error.issues[0]?.message ?? 'Invalid value');
      return false;
    }

    clearActiveCell(cellKey);

    if (Object.is(parsedValue.data, valueBeforeEditRef.current)) {
      resetEditor({ preserveSizeLock });
      return true;
    } else {
      return completeCellCommit({
        currentDraftValue,
        parsedValue: parsedValue.data,
      });
    }
  }

  // The effect below registers the controller once, but these callbacks are recreated each render.
  // Store them in refs so the controller wrappers always call the latest closures without re-registering.
  const activateEditorRef = useRef(activateEditor);
  const submitEditorRef = useRef(submitEditor);
  const cancelEditorRef = useRef(resetEditor);
  activateEditorRef.current = activateEditor;
  submitEditorRef.current = submitEditor;
  cancelEditorRef.current = resetEditor;

  useEffect(() => {
    registerCellController(cellKey, {
      activate: () => activateEditorRef.current(),
      submit: (options) => submitEditorRef.current(undefined, options),
      cancel: (options) => cancelEditorRef.current(options),
      releaseSizeLock: releaseCellSize,
    });

    return () => {
      releaseCellSize();
      registerCellController(cellKey, null);
    };
  }, [cellKey, registerCellController]);

  const isRightAligned = column.meta?.align === 'right';
  const cellSurfaceClassName = cn('min-h-10 w-full min-w-0 px-4 py-2.5', {
    'text-left': !column.meta?.align || column.meta.align === 'left',
    'text-right': isRightAligned,
    'text-center': column.meta?.align === 'center',
  });
  const displayValueClassName = cn(
    "flex min-w-0 flex-1 items-center overflow-hidden text-ellipsis whitespace-nowrap before:text-muted-foreground/50 empty:before:content-['–']",
    {
      'pl-8': isRightAligned,
      'pr-8': !isRightAligned,
      'justify-start': !column.meta?.align || column.meta.align === 'left',
      'justify-end': isRightAligned,
      'justify-center': column.meta?.align === 'center',
    },
  );
  const inputClassName = cn(
    'h-10 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0',
    cellSurfaceClassName,
  );
  const actionTooltip = meta.tooltip?.(value as TValue) ?? null;
  const trailingSlotClassName = cn(
    'pointer-events-none absolute top-1/2 flex -translate-y-1/2 items-center justify-center',
    isRightAligned ? 'left-2' : 'right-2',
  );

  if (mode === 'editing') {
    if (meta.editor.type === 'select') {
      return (
        <EditableSelectInput
          label={label}
          value={draftValue}
          placeholder={meta.editor.placeholder}
          className={inputClassName}
          options={meta.editor.options}
          renderTrigger={meta.editor.renderTrigger}
          validationMessage={validationMessage}
          onCancel={() => {
            resetEditor();
            clearActiveCell(cellKey);
          }}
          onChange={(nextValue) => {
            void submitEditor(nextValue);
          }}
        />
      );
    }

    return (
      <EditableTextInput
        editor={meta.editor}
        label={label}
        value={draftValue}
        className={inputClassName}
        validationMessage={validationMessage}
        onChange={(nextValue) => {
          updateDraftValue(nextValue);
        }}
        onCancel={() => {
          resetEditor();
          clearActiveCell(cellKey);
        }}
        onSubmit={() => {
          void submitEditor();
        }}
      />
    );
  }

  const isButtonDisabled = mode === 'committing' || isRowEditLocked;

  const button = (
    <button
      ref={buttonRef}
      type="button"
      aria-label={`Edit ${label}`}
      disabled={isButtonDisabled}
      className={cn(
        'group relative flex cursor-pointer items-center gap-2 outline-none disabled:pointer-events-none disabled:text-muted-foreground',
        cellSurfaceClassName,
      )}
      onMouseDown={(event) => {
        const activeCellKey = getActiveCellKey();
        if (isButtonDisabled || (!!activeCellKey && activeCellKey !== cellKey)) {
          // this prevents an early focus change (prior to fully opening the cell in onClick)
          event.preventDefault();
        }
      }}
      onClick={() => {
        if (isButtonDisabled) return;
        onRequestEdit({ row: context.row, rowId, columnId, cellKey });
      }}
    >
      <span className={displayValueClassName}>
        {column.cell ? flexRender(column.cell, displayContext) : (value as ReactNode)}
      </span>
      {mode === 'committing' ? (
        <span className={cn(trailingSlotClassName, 'size-6 text-muted-foreground')} aria-hidden>
          <Loader2Icon className="size-3.5 animate-spin" />
        </span>
      ) : (
        <span
          className={cn(
            trailingSlotClassName,
            'size-6 rounded-md border bg-background text-muted-foreground opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100',
          )}
          aria-hidden
        >
          <PencilIcon className="size-4" />
        </span>
      )}
    </button>
  );

  if (!actionTooltip) {
    return button;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{actionTooltip}</TooltipContent>
    </Tooltip>
  );
}
